<script setup lang="ts">
/**
 * The picture of an attachment inside a message or a list: its thumbnail in
 * a box that is reserved from the original's `width` × `height` before the
 * thumbnail arrives, so nothing jumps while it loads (`aspect-ratio`, capped
 * at a sensible width and height, never wider than the original or the
 * container). A video's thumbnail carries a play mark; without a thumbnail a
 * placeholder icon shows the kind. A click or Enter emits `open` —
 * opening the file (e.g. in `NxFilePreviewDialog`) is the caller's.
 *
 * `loading` draws a skeleton in the box. When the thumbnail cannot be loaded
 * the tile emits `error` and shows a placeholder, so the caller can swap it
 * for an `NxAttachmentChip`. Without `width`/`height` the box has a default
 * landscape size. `label` is the accessible name („Bild öffnen: foto.jpg“);
 * attributes (`aria-busy`, `data-testid`) go to the button.
 */
import ImageIcon from 'lucide-vue-next/dist/esm/icons/image.js'; import Play from 'lucide-vue-next/dist/esm/icons/play.js'; import VideoIcon from 'lucide-vue-next/dist/esm/icons/video.js';
import { computed, ref, watch } from 'vue';
import NxSkeleton from '../Loading/NxSkeleton.vue';

const props = withDefaults(
  defineProps<{
    /** Accessible name of the tile. */
    label: string;
    /** Thumbnail URL; unset while it is not known yet. */
    src?: string | null;
    /** Width of the original in pixels; reserves the box with `height`. */
    width?: number | null;
    /** Height of the original in pixels. */
    height?: number | null;
    /** `video` adds the play mark. */
    kind?: 'image' | 'video';
    /** A skeleton instead of the thumbnail. */
    loading?: boolean;
  }>(),
  { src: null, width: null, height: null, kind: 'image', loading: false }
);

const emit = defineEmits<{ open: []; error: [] }>();

const failed = ref(false);
watch(
  () => props.src,
  () => {
    failed.value = false;
  }
);

const hasAspect = computed(
  () => !!props.width && !!props.height && props.width > 0 && props.height > 0
);
const boxStyle = computed<Record<string, string> | undefined>(() =>
  hasAspect.value
    ? {
        '--media-tile-ratio': String(props.width! / props.height!),
        '--media-tile-natural-width': `${props.width}px`,
      }
    : undefined
);
const showImage = computed(() => !props.loading && !!props.src && !failed.value);

function onError() {
  failed.value = true;
  emit('error');
}
</script>

<template>
  <button
    type="button"
    class="nx-media-tile"
    :class="[`kind-${props.kind}`, { 'has-aspect': hasAspect, 'is-failed': failed }]"
    :style="boxStyle"
    :aria-label="props.label"
    data-testid="nx-media-tile"
    @click="emit('open')"
  >
    <NxSkeleton
      v-if="props.loading"
      class="nx-media-tile__skeleton"
      variant="rect"
      width="100%"
      height="100%"
    />
    <img
      v-else-if="showImage"
      class="nx-media-tile__image"
      :src="props.src!"
      alt=""
      decoding="async"
      data-testid="nx-media-tile-image"
      @error="onError"
    />
    <span v-else class="nx-media-tile__placeholder" aria-hidden="true">
      <VideoIcon v-if="props.kind === 'video'" class="nxt-icon-lg" />
      <ImageIcon v-else class="nxt-icon-lg" />
    </span>
    <span
      v-if="props.kind === 'video' && showImage"
      class="nx-media-tile__play"
      aria-hidden="true"
      data-testid="nx-media-tile-play"
    >
      <Play class="nx-media-tile__play-icon nxt-icon-md" />
    </span>
  </button>
</template>

<style scoped>
.nx-media-tile {
  --media-tile-max-width: var(--nxt-panel-width-xl);
  --media-tile-max-height: var(--nxt-area-size-xl);

  position: relative;
  display: block;
  flex: none;
  width: var(--media-tile-max-width);
  max-width: 100%;
  height: var(--nxt-area-size-md);
  padding: 0;
  overflow: hidden;
  border: none;
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-sunken-bg);
  color: var(--nxt-fg-muted);
  cursor: pointer;
}
/* The original's proportions, no wider than the original, the cap or the cap height allows. */
.nx-media-tile.has-aspect {
  width: min(
    var(--media-tile-max-width),
    var(--media-tile-natural-width),
    calc(var(--media-tile-max-height) * var(--media-tile-ratio))
  );
  height: auto;
  aspect-ratio: var(--media-tile-ratio);
}
.nx-media-tile:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}
.nx-media-tile__skeleton {
  display: block;
  border-radius: 0;
}
.nx-media-tile__image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-media-tile:hover .nx-media-tile__image {
  opacity: 0.9;
}
.nx-media-tile__placeholder {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
}
.nx-media-tile__play {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  color: var(--nxt-fg-on-accent);
  pointer-events: none;
}
.nx-media-tile__play-icon {
  box-sizing: content-box;
  padding: var(--nxt-space-xs);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-overlay-bg);
}
@media (prefers-reduced-motion: reduce) {
  .nx-media-tile__image {
    transition: none;
  }
}
</style>
